import type { ComponentProps, CSSProperties } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
} from "../components/ui/alert-dialog"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { resolveIcon } from "./resolve-icon"
import { cn } from "../lib/utils"

type ControlKind = "action" | "cancel"

function AlertDialogControlRenderer({
  element,
  kind,
}: RendererProps & { kind: ControlKind }) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  const lifted = liftV4Props(element)
  const text = lifted.text ? String(resolveSymbol(lifted.text) ?? "") : ""
  const events = lifted.events && typeof lifted.events === "object"
    ? lifted.events as Record<string, unknown>
    : {}
  const hasClickBinding = events.click !== undefined
  const hasTapBinding = events.tap !== undefined
  const eventType = hasClickBinding ? "click" : hasTapBinding ? "tap" : "click"
  const binding = hasClickBinding ? events.click : hasTapBinding ? events.tap : undefined
  const params = binding && typeof binding === "object"
    ? (binding as Record<string, unknown>).params
    : undefined
  const payload = params && typeof params === "object"
    ? { ...(params as Record<string, unknown>), ...(itemContext ?? {}) }
    : { ...(itemContext ?? {}) }
  const iconName = lifted.icon ? String(resolveSymbol(lifted.icon) ?? "") : ""
  const Icon = iconName ? resolveIcon(iconName) : undefined
  const icon = Icon
    ? <Icon className={lifted.iconClassName ?? "size-4"} aria-hidden="true" />
    : null
  const iconRight = lifted.iconPosition === "right"
  const disabled = typeof lifted.disabled === "string"
    ? Boolean(resolveSymbol(lifted.disabled))
    : lifted.disabled === true
  const renderedVariant = typeof lifted.variant === "string"
    ? lifted.variant
    : kind === "cancel"
      ? "outline"
      : "default"

  const sharedProps = {
    className: cn(lifted.className),
    disabled,
    variant: lifted.variant,
    size: lifted.size,
    value: Array.isArray(lifted.value) ? undefined : lifted.value,
    "data-cogo-element-id": lifted.id,
    "data-cogo-type": lifted.type,
    "data-icon": Icon ? iconName : undefined,
    "data-icon-position": Icon ? (iconRight ? "right" : "left") : undefined,
    "data-variant": renderedVariant,
    "data-size": lifted.size ?? "default",
    "data-cogo-event": eventType,
    onClick: () => dispatchEvent(lifted.id, eventType, payload),
  }
  const content = (
    <>
      {!iconRight ? icon : null}
      {text}
      {iconRight ? icon : null}
      {lifted.children?.map((child) => (
        <ElementRenderer key={child.id} element={child} />
      ))}
    </>
  )

  return kind === "action" ? (
    <AlertDialogAction
      {...sharedProps as ComponentProps<typeof AlertDialogAction>}
    >
      {content}
    </AlertDialogAction>
  ) : (
    <AlertDialogCancel
      {...sharedProps as ComponentProps<typeof AlertDialogCancel>}
    >
      {content}
    </AlertDialogCancel>
  )
}

export function AlertDialogContentElementRenderer({ element }: RendererProps) {
  const lifted = liftV4Props(element)
  const authoredSize = typeof lifted.size === "string" ? lifted.size : "default"
  const maxWidth = authoredSize === "sm"
    ? 384
    : authoredSize === "lg"
      ? 672
      : 448

  return (
    <AlertDialogContent
      size={authoredSize === "sm" ? "sm" : "default"}
      className={cn(lifted.className)}
      style={{
        maxWidth,
        ...(lifted.style && typeof lifted.style === "object"
          ? lifted.style as CSSProperties
          : {}),
      }}
      data-size={authoredSize}
      data-cogo-element-id={lifted.id}
      data-cogo-type={lifted.type}
    >
      {lifted.children?.map((child) => (
        <ElementRenderer key={child.id} element={child} />
      ))}
    </AlertDialogContent>
  )
}

export function AlertDialogActionElementRenderer(props: RendererProps) {
  return <AlertDialogControlRenderer {...props} kind="action" />
}

export function AlertDialogCancelElementRenderer(props: RendererProps) {
  return <AlertDialogControlRenderer {...props} kind="cancel" />
}
